export type ToastTone = "success" | "info" | "pending" | "partial" | "error";

export type ToastAction = {
  label: string;
  onClick: () => void;
};

export type ToastInput = {
  tone: ToastTone;
  /** The concrete result: "CSV exportado · 128 itens", never "Sucesso". */
  title: string;
  body?: string;
  /**
   * One follow-up. "Desfazer" only when the operation really can be undone
   * by this callback; otherwise offer the next step ("Abrir arquivo").
   */
  action?: ToastAction;
};

export type Toast = ToastInput & { id: string; createdAt: number };

/** Three on screen at most: toasts must never cover the decision under them. */
export const TOAST_VISIBLE_MAX = 3;
export const TOAST_QUEUE_MAX = 8;

/** Errors and pending work stay until the operator (or the work) closes them. */
export function toastDuration(tone: ToastTone): number | null {
  switch (tone) {
    case "error":
    case "pending":
      return null;
    case "partial":
      return 10_000;
    default:
      return 6_000;
  }
}

export function enqueueToast(queue: readonly Toast[], toast: Toast): Toast[] {
  const next = [...queue, toast];
  if (next.length <= TOAST_QUEUE_MAX) return next;
  // Over the cap, drop the oldest transient toast; errors are never dropped silently.
  const dropIndex = next.findIndex((item) => toastDuration(item.tone) !== null);
  if (dropIndex === -1) return next.slice(next.length - TOAST_QUEUE_MAX);
  return next.filter((_, index) => index !== dropIndex);
}

export function updateToast(
  queue: readonly Toast[],
  id: string,
  patch: Partial<ToastInput>,
): Toast[] {
  return queue.map((item) => (item.id === id ? { ...item, ...patch } : item));
}

export function dismissToast(queue: readonly Toast[], id: string): Toast[] {
  return queue.filter((item) => item.id !== id);
}

/** Oldest first; later toasts wait until an earlier one closes. */
export function visibleToasts(queue: readonly Toast[]): Toast[] {
  return queue.slice(0, TOAST_VISIBLE_MAX);
}
